"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import "./Group.css"
import { API_URL } from "../config"

const EditGroup = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [groupData, setGroupData] = useState({
        name: "",
        groupId: "",
        location: {
            lat: "",
            lng: "",
        },
        selectedDevices: [],
        apiParams: [],
    })

    const [devices, setDevices] = useState([])
    const [selectedDeviceParams, setSelectedDeviceParams] = useState([])
    const [loading, setLoading] = useState(false)
    const [fetching, setFetching] = useState(true)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const [map, setMap] = useState(null)
    const [marker, setMarker] = useState(null)

    const availableParameters = [
        { id: "tmax", name: "Maximum Temperature", unit: "°C", description: "Daily maximum temperature" },
        { id: "ta", name: "Average Temperature", unit: "°C", description: "Daily average temperature" },
        { id: "tmin", name: "Minimum Temperature", unit: "°C", description: "Daily minimum temperature" },
        { id: "rhmax", name: "Maximum Relative Humidity", unit: "%", description: "Daily maximum relative humidity" },
        { id: "rha", name: "Average Relative Humidity", unit: "%", description: "Daily average relative humidity" },
        { id: "rhmin", name: "Minimum Relative Humidity", unit: "%", description: "Daily minimum relative humidity" },
        { id: "uz", name: "Wind Speed at Height Z", unit: "m/s", description: "Wind speed at measurement height" },
        { id: "n", name: "Sunshine Hours", unit: "hours", description: "Daily sunshine duration" },
        { id: "zw", name: "Wind Measurement Height", unit: "m", description: "Height of wind measurement" },
    ]

    // IDs of parameters to hide from API Params selection
    const hiddenApiParamIds = [
        "stationFetch",
        "rs",
        "rn",
        "epan",
        "zp",
        "un",
        "ud",
    ]

    useEffect(() => {
        fetchGroupData()
        fetchDevices()
        loadGoogleMaps()
    }, [])

    useEffect(() => {
        // Update selected device parameters when devices are selected
        const allSelectedParams = []
        groupData.selectedDevices.forEach((deviceId) => {
            const device = devices.find((d) => d._id === deviceId)
            if (device && device.selectedParameters) {
                allSelectedParams.push(...device.selectedParameters)
            }
        })
        setSelectedDeviceParams([...new Set(allSelectedParams)])
    }, [groupData.selectedDevices, devices])

    const loadGoogleMaps = () => {
        if (window.google) {
            initializeMap()
            return
        }

        const script = document.createElement("script")
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCT_w1O7dUdXlSvWCRn-DxU8e11Vd0t94k&libraries=places`
        script.async = true
        script.defer = true
        script.onload = initializeMap
        document.head.appendChild(script)
    }

    const initializeMap = () => {
        if (!groupData.location.lat || !groupData.location.lng) return

        const initialPosition = { 
            lat: parseFloat(groupData.location.lat), 
            lng: parseFloat(groupData.location.lng) 
        }

        const mapInstance = new window.google.maps.Map(document.getElementById("map"), {
            zoom: 10,
            center: initialPosition,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        })

        const markerInstance = new window.google.maps.Marker({
            position: initialPosition,
            map: mapInstance,
            draggable: true,
            title: "Station Location",
        })

        // Add click listener to map
        mapInstance.addListener("click", (event) => {
            const lat = event.latLng.lat()
            const lng = event.latLng.lng()

            markerInstance.setPosition({ lat, lng })
            setGroupData((prev) => ({
                ...prev,
                location: { lat: lat.toString(), lng: lng.toString() },
            }))
        })

        // Add drag listener to marker
        markerInstance.addListener("dragend", (event) => {
            const lat = event.latLng.lat()
            const lng = event.latLng.lng()

            setGroupData((prev) => ({
                ...prev,
                location: { lat: lat.toString(), lng: lng.toString() },
            }))
        })

        setMap(mapInstance)
        setMarker(markerInstance)
    }

    const fetchGroupData = async () => {
        try {
            const token = localStorage.getItem("token")
            const response = await axios.get(`${API_URL}/api/groups/${id}`, {
                headers: { "x-auth-token": token },
            })

            const data = response.data
            setGroupData({
                name: data.name,
                groupId: data.groupId,
                location: {
                    lat: data.location?.coordinates[1]?.toString() || "",
                    lng: data.location?.coordinates[0]?.toString() || "",
                },
                selectedDevices: data.selectedDevices || [],
                apiParams: data.apiParams || [],
            })

            setFetching(false)
        } catch (err) {
            setError("Failed to fetch group data")
            console.error(err)
            setFetching(false)
        }
    }

    const fetchDevices = async () => {
        try {
            const token = localStorage.getItem("token")
            const response = await axios.get(`${API_URL}/api/devices`, {
                headers: { "x-auth-token": token },
            })
            setDevices(response.data)
        } catch (err) {
            setError("Failed to fetch devices")
            console.error(err)
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        if (name.includes("location.")) {
            const locationField = name.split(".")[1]
            const newLocation = { ...groupData.location, [locationField]: value }
            setGroupData((prev) => ({
                ...prev,
                location: newLocation,
            }))

            // Update map if coordinates are valid
            if (map && marker && newLocation.lat && newLocation.lng) {
                const lat = Number.parseFloat(newLocation.lat)
                const lng = Number.parseFloat(newLocation.lng)
                if (!isNaN(lat) && !isNaN(lng)) {
                    const newPos = { lat, lng }
                    map.setCenter(newPos)
                    marker.setPosition(newPos)
                }
            }
        } else {
            setGroupData((prev) => ({ ...prev, [name]: value }))
        }
    }

    const handleDeviceSelection = (deviceId) => {
        setGroupData((prev) => ({
            ...prev,
            selectedDevices: prev.selectedDevices.includes(deviceId)
                ? prev.selectedDevices.filter((id) => id !== deviceId)
                : [...prev.selectedDevices, deviceId],
        }))
    }

    const handleApiParamSelection = (paramId) => {
        setGroupData((prev) => ({
            ...prev,
            apiParams: prev.apiParams.includes(paramId)
                ? prev.apiParams.filter((id) => id !== paramId)
                : [...prev.apiParams, paramId],
        }))
    }

    const getDisabledParams = () => {
        return selectedDeviceParams
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        setError("")
        setSuccess("")

        try {
            const token = localStorage.getItem("token")
            const response = await axios.put(`${API_URL}/api/groups/${id}`, {
                ...groupData,
                location: {
                    type: "Point",
                    coordinates: [
                        parseFloat(groupData.location.lng),
                        parseFloat(groupData.location.lat),
                    ],
                },
            }, {
                headers: { "x-auth-token": token },
            })

            setSuccess("Parameter Station updated successfully!")
            setTimeout(() => {
                navigate(`/group-details/${id}`)
            }, 1500)
        } catch (err) {
            setError(err.response?.data?.msg || "Failed to update parameter Station")
        } finally {
            setLoading(false)
        }
    }

    if (fetching) {
        return (
            <div className="group-page">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading group data...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="group-page">
            <div className="group-container">
                <h1>Edit Parameter Station</h1>

                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                <form onSubmit={handleSubmit} className="group-form">
                    {/* Group Details Section */}
                    <div className="form-section">
                        <h2>Parameter Station Details</h2>
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="name">Station Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={groupData.name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter parameter station name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="groupId">Station ID *</label>
                                <input
                                    type="text"
                                    id="groupId"
                                    name="groupId"
                                    value={groupData.groupId}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Enter unique station ID"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="lat">Latitude *</label>
                                <input
                                    type="number"
                                    id="lat"
                                    name="location.lat"
                                    value={groupData.location.lat}
                                    onChange={handleInputChange}
                                    step="any"
                                    required
                                    placeholder="Enter latitude"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="lng">Longitude *</label>
                                <input
                                    type="number"
                                    id="lng"
                                    name="location.lng"
                                    value={groupData.location.lng}
                                    onChange={handleInputChange}
                                    step="any"
                                    required
                                    placeholder="Enter longitude"
                                />
                            </div>
                        </div>

                        {/* Google Maps Integration */}
                        <div className="map-section">
                            <h3>Select Location on Map</h3>
                            <div id="map" className="google-map"></div>
                            <p className="map-instructions">
                                Click on the map or drag the marker to set the parameter station location
                            </p>
                        </div>
                    </div>

                    {/* Select Source Section */}
                    <div className="form-section">
                        <h2>Select Devices</h2>
                        <p className="section-description">
                            Select devices to include in this parameter station. Device parameters will be automatically included.
                        </p>
                        <div className="devices-grid">
                            {devices.map((device) => (
                                <div
                                    key={device._id}
                                    className={`device-card ${groupData.selectedDevices.includes(device._id) ? "selected" : ""}`}
                                    onClick={() => handleDeviceSelection(device._id)}
                                >
                                    <div className="device-header">
                                        <h3>{device.name}</h3>
                                        <span className="device-code">{device.code}</span>
                                    </div>
                                    <div className="device-info">
                                        <p>Height: {device.height}m</p>
                                        <p>Parameters: {device.selectedParameters?.length || 0}</p>
                                    </div>
                                    {device.selectedParameters && device.selectedParameters.length > 0 && (
                                        <div className="device-params">
                                            <h4>Device Parameters:</h4>
                                            <div className="params-list">
                                                {device.selectedParameters.map((param) => {
                                                    const paramInfo = availableParameters.find((p) => p.id === param)
                                                    return (
                                                        <span key={param} className="param-tag">
                                                            {paramInfo?.name || param}
                                                        </span>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* API Params Section */}
                    <div className="form-section">
                        <h2>Forecasting Parameters</h2>
                        <p className="section-description">
                            Select additional parameters to fetch from weather APIs. Parameters from selected devices are disabled.
                        </p>
                        <div className="params-grid">
                            {availableParameters
                                .filter(param => !hiddenApiParamIds.includes(param.id))
                                .map((param) => {
                                    const isDisabled = selectedDeviceParams.includes(param.id)
                                    const isSelected = groupData.apiParams.includes(param.id)

                                    return (
                                        <div
                                            key={param.id}
                                            className={`param-item ${isDisabled ? "disabled" : ""} ${isSelected ? "selected" : ""}`}
                                        >
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    disabled={isDisabled}
                                                    onChange={() => handleApiParamSelection(param.id)}
                                                />
                                                <div className="param-info">
                                                    <h4>{param.name}</h4>
                                                    <p className="param-unit">{param.unit}</p>
                                                    <p className="param-description">{param.description}</p>
                                                    {isDisabled && <span className="disabled-label">From Device</span>}
                                                </div>
                                            </label>
                                        </div>
                                    )
                                })}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-actions">
                        <button type="submit" disabled={loading} className="btn btn-primary">
                            {loading ? "Updating Parameter Station..." : "Update Parameter Station"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default EditGroup